import type { Fact, FactSource } from './provenance.ts'

/**
 * When a fact stops being an answer.
 *
 * The policy is a number of months per field, which is domain knowledge and
 * stays with each engine's registry. What is here is the arithmetic, which is
 * the same wherever it is applied: a fact's own `stale_after` first, else the
 * policy for its field, else never.
 *
 * Taking the months rather than the field is the whole of what makes this
 * shareable. The caller that knows the field looks the months up; nothing here
 * needs to know that headcount lasts twelve and premises twenty-four.
 */
export interface FactDefinition {
  /** The questionnaire item this answers. */
  question: string
  /** Human-readable name, used when telling the user what needs refreshing. */
  label: string
  /** Months before it stops counting as current. `null` never expires on its own. */
  staleness_months: number | null
}

/** Effective expiry: the fact's own, else the policy passed in, else never. */
export function expiryOf<T>(f: Fact<T>, months: number | null): number | null {
  if (f.stale_after) return Date.parse(f.stale_after)
  if (months === null) return null
  const d = new Date(f.observed_at)
  d.setMonth(d.getMonth() + months)
  return d.getTime()
}

export function isStale<T>(f: Fact<T>, months: number | null, now: number): boolean {
  const expiry = expiryOf(f, months)
  return expiry !== null && now > expiry
}

/** A fact an engine stopped trusting, surfaced so the user knows what to refresh. */
export interface StaleFact<F extends string = string> {
  field: F
  label: string
  question: string
  observed_at: string
  source: FactSource
}
